import type { Asset } from "@cva/shared";
import type { QueryClient } from "@tanstack/react-query";
import { client, orpc } from "./api";

/**
 * Background removal ("Freistellen"): the server makes a cut-out copy of an
 * image (PNG with transparency) next to the original. These helpers start it
 * and wait for it; the media library and both editors use them.
 */

/** Starts it (or returns the copy that already exists or is being made). */
export async function requestCutout(
  queryClient: QueryClient,
  orgId: string,
  assetId: string,
): Promise<Asset> {
  const copy = await client.media.removeBackground({ orgId, assetId });
  // The copy appears in the media library right away ("wird freigestellt"),
  // the activity tray starts polling fast.
  void queryClient.invalidateQueries({ queryKey: orpc.media.list.key() });
  void queryClient.invalidateQueries({ queryKey: orpc.activity.key() });
  return copy;
}

/** Polls the copy until it is ready or failed (or the signal aborts). */
export async function waitForCutout(
  queryClient: QueryClient,
  orgId: string,
  copy: Asset,
  options: { signal?: AbortSignal; onUpdate?: (asset: Asset) => void } = {},
): Promise<Asset> {
  let current = copy;
  // Longer than the slowest run on the small server, including the queue.
  const deadline = Date.now() + 15 * 60_000;
  for (let attempt = 0; current.status === "processing"; attempt++) {
    if (options.signal?.aborted || Date.now() > deadline) return current;
    await new Promise((resolve) => setTimeout(resolve, attempt < 60 ? 1000 : 3000));
    const next = await client.media.get({ orgId, assetId: copy.id }).catch(() => null);
    if (!next) continue;
    current = next;
    options.onUpdate?.(current);
  }
  queryClient.setQueryData(
    orpc.media.get.queryKey({ input: { orgId, assetId: copy.id } }),
    current,
  );
  void queryClient.invalidateQueries({ queryKey: orpc.media.list.key() });
  return current;
}
